/**
 * Admin → Sites (container): the list (live: a site or node change anywhere
 * refetches it), create, rename, delete (confirmed; the server refuses a site
 * with an active node or a studio naming it).
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAdminSites, useCreateSite, useDeleteSite, useRenameSite } from '../../app/hooks.js';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog.js';
import { NameDialog } from '../../components/admin/NameDialog.js';
import { siteHref, SitesTable } from '../../components/admin/SitesTable.js';
import { errorText } from './common.js';

export function SitesAdminView() {
  const sites = useAdminSites();
  const create = useCreateSite();
  const rename = useRenameSite();
  const remove = useDeleteSite();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const nameOf = (siteId: string | null) => sites.data?.find((s) => s.siteId === siteId)?.name ?? siteId ?? '';

  return (
    <>
      <SitesTable
        sites={sites.data ?? []}
        loading={sites.isFetching}
        error={errorText(sites.error)}
        onCreate={() => {
          create.reset();
          setCreating(true);
        }}
        onRetry={() => {
          void sites.refetch();
        }}
        onOpen={(siteId) => {
          void navigate({ to: siteHref(siteId) });
        }}
        onRename={(siteId) => {
          rename.reset();
          setRenaming(siteId);
        }}
        onDelete={(siteId) => {
          remove.reset();
          setDeleting(siteId);
        }}
      />
      <NameDialog
        open={creating}
        title="New site"
        description="A studio location. Add its servers from the site’s page."
        label="Name"
        initial=""
        submitLabel="Create site"
        pending={create.isPending}
        error={errorText(create.error)}
        onSubmit={(name) =>
          create.mutate(
            { name },
            {
              onSuccess: (site) => {
                setCreating(false);
                void navigate({ to: siteHref(site.siteId) });
              },
            },
          )
        }
        onOpenChange={setCreating}
      />
      <NameDialog
        open={renaming !== null}
        title="Rename site"
        description=""
        label="Name"
        initial={nameOf(renaming)}
        submitLabel="Rename"
        pending={rename.isPending}
        error={errorText(rename.error)}
        onSubmit={(name) => {
          if (renaming !== null) rename.mutate({ siteId: renaming, name }, { onSuccess: () => setRenaming(null) });
        }}
        onOpenChange={(open) => (open ? undefined : setRenaming(null))}
      />
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${nameOf(deleting)}?`}
        description="Only a site with no active server and no studio naming it can be deleted; its emergency account goes with it."
        confirmLabel="Delete site"
        typeToConfirm={null}
        pending={remove.isPending}
        error={errorText(remove.error)}
        onConfirm={() => {
          if (deleting !== null)
            remove.mutate(
              { siteId: deleting },
              {
                onSuccess: (site) => {
                  setDeleting(null);
                  toast.success(`Deleted ${site.name}`);
                },
              },
            );
        }}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
      />
    </>
  );
}
